<template>
  <div class="mt-3 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 dark:border-gray-600 dark:bg-gray-800/40">
    <div class="flex flex-col gap-4 sm:flex-row sm:items-center">
      <button
        type="button"
        :disabled="uploading"
        @click="triggerFileDialog"
        class="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-gray-200 bg-white transition-colors hover:border-indigo-400 hover:bg-indigo-50/40 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-900 dark:hover:border-indigo-500 dark:hover:bg-indigo-900/20"
      >
        <img
          v-if="displayPreviewUrl"
          :src="displayPreviewUrl"
          :alt="previewAlt"
          class="max-h-16 max-w-16 object-contain"
        />
        <span v-else class="px-2 text-center text-[11px] leading-4 text-gray-400 dark:text-gray-500">{{ emptyText }}</span>
      </button>

      <div class="min-w-0 flex-1">
        <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ title }}</p>
        <div class="mt-3 flex flex-wrap items-center gap-2">
          <input
            ref="fileInputRef"
            type="file"
            class="hidden"
            accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
            @change="handleFileChange"
          />

          <button
            type="button"
            :disabled="uploading"
            @click="triggerFileDialog"
            class="inline-flex items-center rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800"
          >
            {{ uploading ? '上传中...' : currentUrl ? '重新选择' : '选择图片' }}
          </button>

          <span class="text-xs text-gray-500 dark:text-gray-400">支持 PNG / JPG / JPEG / WEBP，最大 2MB。</span>
        </div>
      </div>
    </div>

    <ImageCropperModal
      :show="showCropModal"
      :source-image-url="selectedSourceUrl"
      :original-file-name="selectedFileName"
      :original-mime-type="selectedMimeType"
      :variant="variant"
      :confirming="uploading"
      @close="closeCropModal"
      @confirm="handleModalConfirm"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'

import ImageCropperModal from '@/components/ImageCropperModal.vue'
import { uploadLogoAPI } from '@/api/tenantConsole'
import toaster from '@/utils/toaster'

type LogoVariant = 'light' | 'dark'

interface Props {
  variant: LogoVariant
  currentUrl: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  uploaded: [payload: { variant: LogoVariant; logoUrl: string; previewUrl: string }]
}>()

const fileInputRef = ref<HTMLInputElement | null>(null)
const uploading = ref(false)
const showCropModal = ref(false)
const selectedSourceUrl = ref('')
const selectedFileName = ref('')
const selectedMimeType = ref('image/png')
const previewOverrideUrl = ref('')
const lastUploadedLogoUrl = ref('')

/**
 * 当前预览地址。
 */
const displayPreviewUrl = computed(() => previewOverrideUrl.value || props.currentUrl)

/**
 * 预览图片 alt。
 */
const previewAlt = computed(() => `${props.variant === 'light' ? '浅色' : '深色'} Logo 预览`)

/**
 * 空状态文案。
 */
const emptyText = computed(() => (props.variant === 'light' ? '暂无浅色 Logo' : '暂无深色 Logo'))

/**
 * 标题文案。
 */
const title = computed(() => (props.variant === 'light' ? '浅色 Logo 上传' : '深色 Logo 上传'))

/**
 * 释放当前源文件预览地址。
 */
const revokeSourceUrl = () => {
  if (selectedSourceUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(selectedSourceUrl.value)
  }
  selectedSourceUrl.value = ''
}

/**
 * 重置当前待编辑文件状态。
 */
const resetSelectedFile = () => {
  revokeSourceUrl()
  selectedFileName.value = ''
  selectedMimeType.value = 'image/png'
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

/**
 * 关闭裁剪弹窗。
 */
const closeCropModal = () => {
  if (uploading.value) {
    return
  }
  showCropModal.value = false
  resetSelectedFile()
}

/**
 * 打开文件选择器。
 */
const triggerFileDialog = () => {
  if (uploading.value) {
    return
  }
  fileInputRef.value?.click()
}

/**
 * 校验待上传文件。
 */
const validateFile = (file: File) => {
  const allowedTypes = ['image/png', 'image/jpeg', 'image/webp']
  const allowedExtensions = ['png', 'jpg', 'jpeg', 'webp']
  const extension = file.name.includes('.') ? file.name.split('.').pop()?.toLowerCase() || '' : ''

  if ((file.type && !allowedTypes.includes(file.type)) || !allowedExtensions.includes(extension)) {
    throw new Error('仅支持 PNG、JPG、JPEG、WEBP 格式的图片')
  }

  if (file.size > 2 * 1024 * 1024) {
    throw new Error('Logo 文件大小不能超过 2MB')
  }
}

/**
 * 选择原图后打开裁剪弹窗。
 */
const handleFileChange = (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) {
    return
  }

  try {
    validateFile(file)
  } catch (error) {
    resetSelectedFile()
    toaster.error((error as Error).message)
    return
  }

  resetSelectedFile()
  selectedFileName.value = file.name
  selectedMimeType.value = file.type || 'image/png'
  selectedSourceUrl.value = URL.createObjectURL(file)
  showCropModal.value = true
}

/**
 * 确认裁剪后上传最终文件。
 */
const handleModalConfirm = async (file: File) => {
  uploading.value = true

  try {
    const response = await uploadLogoAPI(file, props.variant)

    if (response.status !== 200 || !response.data) {
      throw new Error(response.message || '上传 Logo 失败')
    }
    
    toaster.success('上传 Logo 成功')
    previewOverrideUrl.value = response.data.previewUrl || ''
    lastUploadedLogoUrl.value = response.data.logoUrl
    emit('uploaded', {
      variant: props.variant,
      logoUrl: response.data.logoUrl,
      previewUrl: response.data.previewUrl || '',
    })
    showCropModal.value = false
    resetSelectedFile()
  } catch (error) {
    toaster.error((error as Error).message || '上传 Logo 失败')
  } finally {
    uploading.value = false
  }
}

watch(
  () => props.currentUrl,
  (nextUrl) => {
    if (!nextUrl) {
      previewOverrideUrl.value = ''
      lastUploadedLogoUrl.value = ''
      return
    }

    if (lastUploadedLogoUrl.value && nextUrl !== lastUploadedLogoUrl.value) {
      previewOverrideUrl.value = ''
      lastUploadedLogoUrl.value = ''
    }
  },
)

onBeforeUnmount(() => {
  resetSelectedFile()
})
</script>
